import React, { useMemo, useState } from 'react';
import { TrendingUp, TrendingDown, Zap, AlertTriangle, ChevronRight, X } from 'lucide-react';

const ASSET_COLORS_HEX = {
  'ETF': '#041627',
  'Stock': '#6bfe9c',
  'FCI': '#e9c349',
  'Liquidity': '#94a3b8',
  'Time Deposit': '#5b8dee',
  'USD Purchase': '#f97316',
  'Other': '#c4c6cd'
};

const parseDate = (dateStr) => {
  if (!dateStr) return new Date(0);
  if (typeof dateStr === 'string' && dateStr.includes('/')) {
    const parts = dateStr.split('/');
    return new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
  }
  return new Date(dateStr);
};

const DonutChart = ({ items, profileLabel }) => {
  const r = 68;
  const cx = 90;
  const cy = 90;
  const circumference = 2 * Math.PI * r;
  // Normalize so percentages always sum to exactly 100 — avoids grey gap from float rounding
  const totalPct = items.reduce((s, it) => s + it.percentage, 0) || 100;
  let cumulative = 0;
  const segments = items.map((item, idx) => {
    const normalizedPct = (item.percentage / totalPct) * 100;
    // Last segment fills whatever arc remains — eliminates floating-point grey gap
    const dash = idx === items.length - 1
      ? Math.max(circumference - cumulative, 0)
      : (normalizedPct / 100) * circumference;
    const offset = cumulative === 0 ? 0 : circumference - cumulative;
    const seg = { ...item, dash, offset };
    cumulative += dash;
    return seg;
  });
  return (
    <svg width="180" height="180" viewBox="0 0 180 180">
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#f1f5f9" strokeWidth="20" />
      {segments.map((seg) => (
        <circle key={seg.type} cx={cx} cy={cy} r={r} fill="none"
          stroke={ASSET_COLORS_HEX[seg.type] || '#c4c6cd'}
          strokeWidth="20"
          strokeDasharray={`${seg.dash} ${circumference}`}
          strokeDashoffset={seg.offset}
          transform={`rotate(-90 ${cx} ${cy})`}
        />
      ))}
      <text x={cx} y={cy - 10} textAnchor="middle" fontSize="8" fill="#64748b" fontWeight="700" letterSpacing="1.5">DIVERSIFIED</text>
      <text x={cx} y={cy + 8} textAnchor="middle" fontSize="13" fill="#041627" fontWeight="800">{profileLabel}</text>
    </svg>
  );
};

const LineChart = ({ data, formatCurrency }) => {
  const [hoveredIdx, setHoveredIdx] = useState(null);
  if (!data || data.length < 2) return <div className="h-40 flex items-center justify-center text-slate-400 text-sm">Sin datos históricos</div>;
  const values = data.map(d => d.estimatedValue);
  const minVal = Math.min(...values);
  const maxVal = Math.max(...values);
  const range = maxVal - minVal || 1;
  const W = 600, H = 160;
  const PAD_T = 24, PAD_R = 10, PAD_B = 10, PAD_L = 10;
  const chartW = W - PAD_L - PAD_R;
  const chartH = H - PAD_T - PAD_B;
  const getX = (i) => PAD_L + (i / (data.length - 1)) * chartW;
  const getY = (v) => PAD_T + (1 - (v - minVal) / range) * chartH;
  const points = data.map((d, i) => ({ x: getX(i), y: getY(d.estimatedValue) }));
  let pathD = `M ${points[0].x},${points[0].y}`;
  for (let i = 1; i < points.length; i++) {
    const cpx = (points[i - 1].x + points[i].x) / 2;
    pathD += ` C ${cpx},${points[i - 1].y} ${cpx},${points[i].y} ${points[i].x},${points[i].y}`;
  }
  const lastPt = points[points.length - 1];
  const areaD = `${pathD} L ${lastPt.x},${H - PAD_B} L ${PAD_L},${H - PAD_B} Z`;
  return (
    <div className="relative w-full">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: '160px' }} onMouseLeave={() => setHoveredIdx(null)}>
        <defs>
          <linearGradient id="lineAreaGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#6bfe9c" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#6bfe9c" stopOpacity="0.02" />
          </linearGradient>
        </defs>
        {[0.25, 0.5, 0.75].map(t => (
          <line key={t} x1={PAD_L} y1={PAD_T + t * chartH} x2={W - PAD_R} y2={PAD_T + t * chartH} stroke="#e2e8f0" strokeWidth="1" />
        ))}
        <path d={areaD} fill="url(#lineAreaGrad)" />
        <path d={pathD} fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        {points.map((pt, i) => (
          <rect key={i} x={pt.x - 14} y={PAD_T} width="28" height={chartH} fill="transparent" onMouseEnter={() => setHoveredIdx(i)} />
        ))}
        {hoveredIdx !== null && (
          <>
            <line x1={points[hoveredIdx].x} y1={PAD_T} x2={points[hoveredIdx].x} y2={H - PAD_B} stroke="#94a3b8" strokeWidth="1" strokeDasharray="4 3" />
            <circle cx={points[hoveredIdx].x} cy={points[hoveredIdx].y} r="5" fill="#041627" stroke="#22c55e" strokeWidth="2.5" />
          </>
        )}
      </svg>
      {hoveredIdx !== null && (() => {
        const d = data[hoveredIdx];
        const pt = points[hoveredIdx];
        const leftPct = ((pt.x - PAD_L) / chartW) * 100;
        const topPx = Math.max(0, pt.y - 70);
        return (
          <div className="absolute pointer-events-none z-20" style={{ top: topPx, left: `${leftPct}%`, transform: 'translateX(-50%)' }}>
            <div className="bg-[#041627] text-white text-[11px] px-3 py-2 rounded-lg shadow-xl whitespace-nowrap">
              <div className="font-bold uppercase tracking-wide text-slate-300">{d.month}</div>
              <div className="text-[#6bfe9c] font-bold text-sm">{formatCurrency(d.estimatedValue)}</div>
              {d.cumulativeInvested > 0 && (
                <div className={`mt-0.5 ${d.returnAmount >= 0 ? 'text-green-300' : 'text-red-300'}`}>
                  {d.returnAmount >= 0 ? '+' : ''}{formatCurrency(d.returnAmount)}
                </div>
              )}
            </div>
            <div className="w-0 h-0 mx-auto border-l-[5px] border-r-[5px] border-t-[5px] border-l-transparent border-r-transparent border-t-[#041627]"></div>
          </div>
        );
      })()}
    </div>
  );
};

const AssetPriceChart = ({ holding, operations, priceHistory, currentPrices, formatCurrency }) => {
  const [hoveredIdx, setHoveredIdx] = useState(null);

  const parseLocalDate = (dateStr) => {
    if (!dateStr) return new Date();
    if (dateStr.includes('/')) {
      const [d, m, y] = dateStr.split('/').map(Number);
      return new Date(y, m - 1, d);
    }
    const parts = dateStr.split('-').map(Number);
    return new Date(parts[0], parts[1] - 1, parts[2]);
  };

  const fmtDate = (dateStr) => {
    const dt = parseLocalDate(dateStr);
    return dt.toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: '2-digit' });
  };

  const buyOps = operations
    .filter(op => op.name === holding.name && op.type === 'Buy')
    .map(op => ({
      date: op.date,
      price: parseFloat(op.price),
      type: 'buy',
      label: `Compra · ${parseFloat(op.quantity).toLocaleString('es-CL')} uds.`
    }));

  const updates = (priceHistory[holding.name] || []).map(u => ({
    date: u.date,
    price: u.price,
    type: 'update',
    label: 'Actualización de precio'
  }));

  const merged = [...buyOps, ...updates].sort((a, b) => parseLocalDate(a.date) - parseLocalDate(b.date));

  const currentPrice = currentPrices[holding.name];
  if (currentPrice) {
    const lastPrice = merged.length > 0 ? merged[merged.length - 1].price : null;
    if (!lastPrice || Math.abs(currentPrice - lastPrice) > 0.001) {
      const today = new Date().toISOString().split('T')[0];
      merged.push({ date: today, price: currentPrice, type: 'current', label: 'Precio actual' });
    }
  }

  const allPoints = merged;

  if (allPoints.length < 1) {
    return (
      <div className="text-sm text-slate-400 text-center py-10">
        Sin historial. Actualizá el precio en <strong>Update Prices</strong> para registrar la evolución.
      </div>
    );
  }

  const prices = allPoints.map(p => p.price);
  const rawMin = Math.min(...prices);
  const rawMax = Math.max(...prices);
  const pad = (rawMax - rawMin) * 0.12 || rawMax * 0.08 || 1;
  const minP = rawMin - pad;
  const maxP = rawMax + pad;
  const range = maxP - minP;

  const W = 560, H = 210;
  const PAD_T = 20, PAD_R = 20, PAD_B = 32, PAD_L = 20;
  const chartW = W - PAD_L - PAD_R;
  const chartH = H - PAD_T - PAD_B;

  const getX = (i) => allPoints.length === 1
    ? PAD_L + chartW / 2
    : PAD_L + (i / (allPoints.length - 1)) * chartW;
  const getY = (p) => PAD_T + (1 - (p - minP) / range) * chartH;

  const points = allPoints.map((d, i) => ({ x: getX(i), y: getY(d.price) }));

  let pathD = '';
  if (points.length >= 2) {
    pathD = `M ${points[0].x},${points[0].y}`;
    for (let i = 1; i < points.length; i++) {
      const cpx = (points[i - 1].x + points[i].x) / 2;
      pathD += ` C ${cpx},${points[i - 1].y} ${cpx},${points[i].y} ${points[i].x},${points[i].y}`;
    }
  }
  const areaD = pathD
    ? `${pathD} L ${points[points.length - 1].x},${H - PAD_B} L ${PAD_L},${H - PAD_B} Z`
    : '';

  const buyPricePoints = allPoints.filter(p => p.type === 'buy');
  const avgBuy = buyPricePoints.length > 0
    ? buyPricePoints.reduce((s, p) => s + p.price, 0) / buyPricePoints.length
    : null;
  const avgBuyY = avgBuy !== null ? getY(avgBuy) : null;

  return (
    <div className="relative w-full mt-1">
      <div className="flex gap-4 mb-3 flex-wrap">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full bg-[#6bfe9c] border-2 border-[#006d37] flex-shrink-0" />
          <span className="text-[11px] text-slate-500">Punto de compra</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full bg-[#041627] flex-shrink-0" />
          <span className="text-[11px] text-slate-500">Actualización de precio</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full bg-[#5b8dee] flex-shrink-0" />
          <span className="text-[11px] text-slate-500">Precio actual</span>
        </div>
        {avgBuy !== null && (
          <div className="flex items-center gap-1.5">
            <div className="w-5 h-0 border-t-2 border-dashed border-amber-400 flex-shrink-0" />
            <span className="text-[11px] text-slate-500">Costo promedio ({formatCurrency(avgBuy)})</span>
          </div>
        )}
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: '210px' }} onMouseLeave={() => setHoveredIdx(null)}>
        <defs>
          <linearGradient id="assetGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#6bfe9c" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#6bfe9c" stopOpacity="0.02" />
          </linearGradient>
        </defs>
        {[0, 0.25, 0.5, 0.75, 1].map(t => (
          <line key={t} x1={PAD_L} y1={PAD_T + t * chartH} x2={W - PAD_R} y2={PAD_T + t * chartH} stroke="#f1f5f9" strokeWidth="1" />
        ))}
        {avgBuyY !== null && avgBuyY >= PAD_T && avgBuyY <= H - PAD_B && (
          <line x1={PAD_L} y1={avgBuyY} x2={W - PAD_R} y2={avgBuyY} stroke="#fbbf24" strokeWidth="1.5" strokeDasharray="6 4" opacity="0.85" />
        )}
        {areaD && <path d={areaD} fill="url(#assetGrad)" />}
        {pathD && <path d={pathD} fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />}
        {points.map((pt, i) => {
          const type = allPoints[i].type;
          return (
            <g key={i}>
              {type === 'buy' ? (
                <circle cx={pt.x} cy={pt.y} r="7" fill="#6bfe9c" stroke="#006d37" strokeWidth="2" />
              ) : type === 'current' ? (
                <circle cx={pt.x} cy={pt.y} r="5" fill="#5b8dee" stroke="white" strokeWidth="2" />
              ) : (
                <circle cx={pt.x} cy={pt.y} r="4.5" fill="#041627" stroke="white" strokeWidth="1.5" />
              )}
              <rect
                x={Math.max(PAD_L, pt.x - 24)} y={PAD_T} width="48" height={chartH}
                fill="transparent" onMouseEnter={() => setHoveredIdx(i)}
              />
            </g>
          );
        })}
        {hoveredIdx !== null && (
          <line x1={points[hoveredIdx].x} y1={PAD_T} x2={points[hoveredIdx].x} y2={H - PAD_B} stroke="#94a3b8" strokeWidth="1" strokeDasharray="4 3" />
        )}
        {allPoints.map((d, i) => {
          const step = Math.max(1, Math.ceil(allPoints.length / 5));
          if (i % step !== 0 && i !== allPoints.length - 1) return null;
          return (
            <text key={i} x={points[i].x} y={H - 6} textAnchor="middle" fontSize="9" fill="#94a3b8">
              {fmtDate(d.date)}
            </text>
          );
        })}
      </svg>
      {hoveredIdx !== null && (() => {
        const d = allPoints[hoveredIdx];
        const pt = points[hoveredIdx];
        const leftFrac = (pt.x - PAD_L) / chartW;
        const transformX = leftFrac < 0.2 ? '0%' : leftFrac > 0.8 ? '-100%' : '-50%';
        return (
          <div
            className="absolute pointer-events-none z-20"
            style={{ top: Math.max(0, pt.y - 100), left: `${leftFrac * 100}%`, transform: `translateX(${transformX})` }}
          >
            <div className="bg-[#041627] text-white text-[11px] px-3 py-2 rounded-lg shadow-xl whitespace-nowrap">
              <div className="font-bold text-slate-300 text-[10px] uppercase tracking-wide">{d.label}</div>
              <div className="text-[#6bfe9c] font-bold text-base">{formatCurrency(d.price)}</div>
              <div className="text-slate-400 mt-0.5">{fmtDate(d.date)}</div>
              {avgBuy !== null && d.price !== avgBuy && (
                <div className={`mt-1 font-semibold text-[11px] ${d.price >= avgBuy ? 'text-green-400' : 'text-red-400'}`}>
                  {d.price >= avgBuy ? '+' : ''}{(((d.price - avgBuy) / avgBuy) * 100).toFixed(2)}% vs costo promedio
                </div>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
};

const DashboardView = ({ formatCurrency, usdClpRate, operations, currentPrices, investorProfile = 'moderate', priceHistory = {} }) => {
  const [selectedHolding, setSelectedHolding] = useState(null);
  const [showAllHoldings, setShowAllHoldings] = useState(false);
  // Calculate portfolio metrics
  const buyOperations = operations.filter(op => op.type === 'Buy');
  const sellOperations = operations.filter(op => op.type === 'Sell');

  // For USD Purchase: the current value in CLP = qty * usdClpRate (mark-to-market)
  // For everything else: use currentPrices[name], falling back to buy price
  const getEffectivePrice = (op) => {
    if (op.assetType === 'USD Purchase') return usdClpRate;
    const rawPrice = currentPrices[op.name] || parseFloat(op.price || 0);
    if (op.operationCurrency === 'USD') return rawPrice * usdClpRate;
    return rawPrice;
  };

  const totalInvested = buyOperations.reduce((sum, op) => {
    // Use purchaseRate (rate at time of buy) so cost basis is fixed in CLP
    const rate = op.purchaseRate || usdClpRate;
    const priceInCLP = op.operationCurrency === 'USD'
      ? parseFloat(op.price || 0) * rate
      : parseFloat(op.price || 0);
    return sum + (parseFloat(op.quantity || 0) * priceInCLP);
  }, 0);

  const currentValue = buyOperations.reduce((sum, op) => {
    return sum + (parseFloat(op.quantity || 0) * getEffectivePrice(op));
  }, 0);

  const totalSold = sellOperations.reduce((sum, op) => {
    return sum + (parseFloat(op.quantity || 0) * parseFloat(op.price || 0));
  }, 0);

  const portfolioValue = currentValue - totalSold;
  const totalReturn = portfolioValue - totalInvested;
  const returnPercentage = totalInvested > 0 ? (totalReturn / totalInvested) * 100 : 0;

  // Calculate asset allocation
  const allocationByType = {};
  buyOperations.forEach(op => {
    const value = parseFloat(op.quantity || 0) * getEffectivePrice(op);
    const type = op.assetType || 'Other';
    if (!allocationByType[type]) allocationByType[type] = 0;
    allocationByType[type] += value;
  });

  const totalAllocationValue = Object.values(allocationByType).reduce((sum, val) => sum + val, 0);

  // Create allocation items for display
  const allocationItems = Object.entries(allocationByType)
    .map(([type, value]) => ({
      type,
      value,
      percentage: totalAllocationValue > 0 ? (value / totalAllocationValue) * 100 : 0
    }))
    .sort((a, b) => b.value - a.value); // Sort by value descending

  // Generate monthly investment and returns data (last 12 months)
  const historicalData = useMemo(() => {
    const months = [];
    const now = new Date();

    for (let i = 11; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const nextDate = new Date(now.getFullYear(), now.getMonth() - i + 1, 1);
      const monthName = date.toLocaleDateString('es-CL', { month: 'short', year: '2-digit' });

      let newInvestment = 0;      // Money newly invested this month only
      let cumulativeInvested = 0; // Total invested up to end of this month
      let estimatedValue = 0;     // Estimated portfolio value at this month

      buyOperations.forEach(op => {
        // Parse date: handle both ISO (2026-03-21) and es-CL (21/3/2026) formats
        let opDate;
        if (op.date && op.date.includes('/')) {
          const parts = op.date.split('/');
          opDate = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
        } else {
          opDate = new Date(op.date);
        }
        if (opDate < nextDate) {
          const priceInCLP = op.operationCurrency === 'USD'
            ? parseFloat(op.price || 0) * usdClpRate
            : parseFloat(op.price || 0);
          const cost = parseFloat(op.quantity || 0) * priceInCLP;
          cumulativeInvested += cost;

          if (opDate >= date) {
            newInvestment += cost;
          }

          // Use real price history: find the most recent price update at or before
          // this month's end. Fall back to current price for the current month,
          // or the original buy price when no history exists for past months.
          const history = (priceHistory[op.name] || [])
            .filter(h => new Date(h.date) <= nextDate)
            .sort((a, b) => new Date(b.date) - new Date(a.date));
          let priceAtMonth;
          if (history.length > 0) {
            priceAtMonth = op.operationCurrency === 'USD'
              ? history[0].price * usdClpRate
              : history[0].price;
          } else {
            const now = new Date();
            const isCurrentMonth = nextDate > new Date(now.getFullYear(), now.getMonth(), 1);
            priceAtMonth = isCurrentMonth
              ? getEffectivePrice(op)
              : priceInCLP;
          }
          estimatedValue += parseFloat(op.quantity || 0) * priceAtMonth;
        }
      });

      const returnAmount = estimatedValue - cumulativeInvested;
      const returnPercentage = cumulativeInvested > 0 ? (returnAmount / cumulativeInvested) * 100 : 0;

      months.push({
        month: monthName,
        newInvestment,
        cumulativeInvested,
        estimatedValue,
        returnAmount,
        returnPercentage
      });
    }

    return months;
  }, [buyOperations, currentPrices, priceHistory]);

  // Liquidity %
  const liquidityValue = buyOperations
    .filter(op => op.assetType === 'Liquidity' || op.assetType === 'FCI')
    .reduce((sum, op) => {
      return sum + parseFloat(op.quantity || 0) * getEffectivePrice(op);
    }, 0);
  const liquidityPct = portfolioValue > 0 ? (liquidityValue / portfolioValue) * 100 : 0;

  // Top holdings grouped by asset name
  const holdingsByName = {};
  buyOperations.forEach(op => {
    const n = op.name;
    if (!holdingsByName[n]) holdingsByName[n] = { name: n, assetType: op.assetType, operationCurrency: op.operationCurrency, qty: 0, cost: 0 };
    holdingsByName[n].qty += parseFloat(op.quantity || 0);
    const priceCLP = op.operationCurrency === 'USD'
      ? parseFloat(op.price || 0) * usdClpRate
      : parseFloat(op.price || 0);
    holdingsByName[n].cost += parseFloat(op.quantity || 0) * priceCLP;
  });
  sellOperations.forEach(op => {
    if (holdingsByName[op.name]) holdingsByName[op.name].qty -= parseFloat(op.quantity || 0);
  });
  const allHoldings = Object.values(holdingsByName)
    .filter(h => h.qty > 0)
    .map(h => {
      const fallbackCLP = h.qty > 0 ? h.cost / h.qty : 0;
      const p = h.assetType === 'USD Purchase'
        ? usdClpRate
        : h.operationCurrency === 'USD'
          ? (currentPrices[h.name] ? currentPrices[h.name] * usdClpRate : fallbackCLP)
          : (currentPrices[h.name] || fallbackCLP);
      const val = h.qty * p;
      const ret = h.cost > 0 ? ((val - h.cost) / h.cost) * 100 : 0;
      const returnAmount = val - h.cost;
      return { ...h, currentVal: val, returnPct: ret, returnAmount };
    })
    .sort((a, b) => b.currentVal - a.currentVal);
  const topHoldings = allHoldings.slice(0, 3);
  const displayedHoldings = showAllHoldings ? allHoldings : topHoldings;

  // Monthly return (last month delta)
  const monthlyReturn = historicalData.length >= 2
    ? historicalData[historicalData.length - 1].estimatedValue - historicalData[historicalData.length - 2].estimatedValue
    : 0;

  // Investment strategy based on profile
  const strategyMap = {
    conservative: { name: 'Capital Preservation', desc: 'Prioriza preservación del capital con activos de bajo riesgo. Enfoque en bonos y depósitos fijos.' },
    moderate: { name: 'Crecimiento Balanceado', desc: 'Mix equilibrado entre renta fija y variable. Diversificación para rendimiento moderado.' },
    aggressive: { name: 'Crecimiento Agresivo', desc: 'Foco en activos de alto rendimiento. Exposición a tecnología y mercados emergentes.' }
  };
  const strategy = strategyMap[investorProfile] || strategyMap.moderate;
  const profileLabels = { conservative: 'Low Risk', moderate: 'Moderate', aggressive: 'High Risk' };

  // Dynamic alerts
  const alerts = [];
  if (liquidityPct < 5 && portfolioValue > 0) alerts.push({ msg: 'Liquidez por debajo del umbral del 5%.' });
  if (allocationItems.length > 0 && allocationItems[0].percentage > 70) alerts.push({ msg: `Alta concentración en ${allocationItems[0].type} (${allocationItems[0].percentage.toFixed(0)}%).` });
  if (returnPercentage < -5) alerts.push({ msg: `Rendimiento negativo del portafolio: ${returnPercentage.toFixed(1)}%.` });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
        <div>
          <label className="text-xs font-semibold text-[#44474c] uppercase tracking-wider mb-1 block">Total Portfolio Value</label>
          <div className="flex items-baseline gap-3">
            <h1 className="text-3xl md:text-5xl font-extrabold tracking-tighter text-[#041627]">
              {formatCurrency(portfolioValue)}
            </h1>
            <span className={`px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1 ${
              totalReturn >= 0 ? 'bg-[#6bfe9c] text-[#005228]' : 'bg-red-100 text-red-700'
            }`}>
              {totalReturn >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
              {totalReturn >= 0 ? '+' : ''}{returnPercentage.toFixed(1)}%
            </span>
          </div>
        </div>
        <div className="flex flex-wrap gap-4">
          <div className="bg-white rounded-xl px-5 py-4 shadow-sm border border-slate-100 min-w-[140px]">
            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Retorno Mensual</p>
            <p className={`text-xl font-bold mt-1 ${monthlyReturn >= 0 ? 'text-[#006d37]' : 'text-red-600'}`}>
              {monthlyReturn >= 0 ? '+' : ''}{formatCurrency(monthlyReturn)}
            </p>
          </div>
          <div className="bg-white rounded-xl px-5 py-4 shadow-sm border border-slate-100 min-w-[120px]">
            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Liquidez</p>
            <p className={`text-xl font-bold mt-1 ${liquidityPct < 5 ? 'text-red-600' : 'text-[#041627]'}`}>
              {liquidityPct.toFixed(1)}%
            </p>
          </div>
        </div>
      </div>

      {/* Row 1: Line Chart + Asset Allocation */}
      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-8 bg-white rounded-xl p-6 shadow-sm border border-slate-100">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-bold text-[#041627]">Rendimiento Histórico</h3>
            <div className="flex gap-1.5">
              <button className="text-[10px] px-3 py-1 rounded-md bg-[#041627] text-white font-semibold">1Y</button>
              <button className="text-[10px] px-3 py-1 rounded-md text-slate-500 hover:bg-slate-100">ALL</button>
            </div>
          </div>
          <LineChart data={historicalData} formatCurrency={formatCurrency} />
          <div className="flex justify-between mt-2 px-1">
            {historicalData.filter((_, i) => i % 2 === 0).map((d, i) => (
              <span key={i} className="text-[10px] text-slate-400">{d.month}</span>
            ))}
          </div>
        </div>

        <div className="col-span-12 lg:col-span-4 bg-white rounded-xl p-6 shadow-sm border border-slate-100">
          <h3 className="font-bold text-[#041627] mb-4">Asset Allocation</h3>
          <div className="flex flex-col items-center">
            {allocationItems.length > 0 ? (
              <DonutChart items={allocationItems} profileLabel={profileLabels[investorProfile] || 'Active'} />
            ) : (
              <div className="w-44 h-44 rounded-full border-[16px] border-slate-100 flex items-center justify-center">
                <p className="text-xs text-slate-400">Sin activos</p>
              </div>
            )}
            <div className="grid grid-cols-2 w-full mt-4 gap-y-2 gap-x-3">
              {allocationItems.map((item) => (
                <div key={item.type} className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: ASSET_COLORS_HEX[item.type] || '#c4c6cd' }} />
                  <span className="text-xs text-slate-600">{item.type} ({item.percentage.toFixed(0)}%)</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Row 2: Top Holdings + Strategy + Alerts */}
      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-7 bg-white rounded-xl p-6 shadow-sm border border-slate-100">
          <div className="flex justify-between items-center mb-5">
            <h3 className="font-bold text-[#041627]">Top Holdings</h3>
            <button
              onClick={() => setShowAllHoldings(v => !v)}
              className="text-[10px] font-semibold text-[#006d37] uppercase tracking-wide flex items-center gap-0.5 hover:underline"
            >
              {showAllHoldings ? 'Ver menos' : 'Ver todos'} <ChevronRight size={12} />
            </button>
          </div>
          {displayedHoldings.length > 0 ? (
            <div className="space-y-1">
              {displayedHoldings.map((h) => (
                <div
                  key={h.name}
                  onClick={() => setSelectedHolding(h)}
                  className="flex items-center justify-between py-3 border-b border-slate-50 last:border-0 cursor-pointer hover:bg-slate-50 rounded-lg px-2 -mx-2 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white font-bold text-xs flex-shrink-0"
                      style={{ backgroundColor: ASSET_COLORS_HEX[h.assetType] || '#94a3b8' }}>
                      {h.name.slice(0, 2).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-semibold text-sm text-[#041627]">{h.name}</p>
                      <p className="text-[11px] text-slate-400">{h.assetType} · {parseFloat(h.qty).toLocaleString('es-CL')} uds.</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-sm text-[#041627]">{formatCurrency(h.currentVal)}</p>
                    <p className={`text-[11px] font-semibold ${h.returnPct >= 0 ? 'text-[#006d37]' : 'text-red-600'}`}>
                      {h.returnPct >= 0 ? '+' : ''}{h.returnPct.toFixed(1)}%
                      <span className="ml-1 font-normal opacity-75">
                        ({h.returnAmount >= 0 ? '+' : ''}{formatCurrency(h.returnAmount)})
                      </span>
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-400 text-center py-8">No hay activos en el portafolio</p>
          )}
        </div>

        <div className="col-span-12 lg:col-span-5 flex flex-col gap-4">
          <div className="bg-[#041627] rounded-xl p-6 flex-1">
            <div className="flex items-start justify-between">
              <p className="text-[10px] font-bold text-[#6bfe9c] uppercase tracking-widest">Estrategia de Inversión</p>
              <Zap size={18} className="text-[#6bfe9c]" />
            </div>
            <h3 className="text-xl font-extrabold text-white mt-2">{strategy.name}</h3>
            <p className="text-sm text-slate-400 mt-2 leading-relaxed">{strategy.desc}</p>
            <button className="mt-4 bg-[#6bfe9c] text-[#041627] text-xs font-bold px-4 py-2 rounded-lg hover:bg-[#4de880] transition-colors">
              Rebalancear Ahora
            </button>
          </div>

          {alerts.length > 0 ? (
            <div className="space-y-2">
              {alerts.map((alert, i) => (
                <div key={i} className="bg-white border border-slate-100 rounded-xl px-4 py-3 flex items-center gap-3 shadow-sm">
                  <AlertTriangle size={16} className="text-amber-500 flex-shrink-0" />
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Alerta de Portafolio</p>
                    <p className="text-xs font-semibold text-[#041627]">{alert.msg}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white border border-slate-100 rounded-xl px-4 py-3 flex items-center gap-3 shadow-sm">
              <TrendingUp size={16} className="text-[#6bfe9c]" />
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Estado del Portafolio</p>
                <p className="text-xs font-semibold text-[#041627]">Sin alertas activas.</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Asset Price History Modal */}
      {selectedHolding && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedHolding(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-xs flex-shrink-0"
                    style={{ backgroundColor: ASSET_COLORS_HEX[selectedHolding.assetType] || '#94a3b8' }}
                  >
                    {selectedHolding.name.slice(0, 2).toUpperCase()}
                  </div>
                  <h3 className="text-lg font-bold text-[#041627]">{selectedHolding.name}</h3>
                </div>
                <p className="text-xs text-slate-400 ml-10">{selectedHolding.assetType} · {parseFloat(selectedHolding.qty).toLocaleString('es-CL')} uds.</p>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="text-xl font-bold text-[#041627]">{formatCurrency(selectedHolding.currentVal)}</p>
                  <p className={`text-sm font-semibold ${selectedHolding.returnPct >= 0 ? 'text-[#006d37]' : 'text-red-600'}`}>
                    {selectedHolding.returnPct >= 0 ? '+' : ''}{selectedHolding.returnPct.toFixed(2)}% retorno total
                  </p>
                </div>
                <button
                  onClick={() => setSelectedHolding(null)}
                  className="text-slate-400 hover:text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-full p-1.5 transition-colors"
                >
                  <X size={16} />
                </button>
              </div>
            </div>
            <p className="text-xs text-slate-500 mb-4 font-medium">Evolución del precio — puntos de compra y actualizaciones registradas</p>
            <AssetPriceChart
              holding={selectedHolding}
              operations={operations}
              priceHistory={priceHistory}
              currentPrices={currentPrices}
              formatCurrency={formatCurrency}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardView;
